import {
  setQueryStringParam,
  deleteQueryStringParam,
  getQueryStringParams,
} from "../../shared/utils";

/**
 * Reusable Alpine.js mixin for people filtering functionality
 *
 * Provides consistent handling of both team (squad) and group (Frontline::UserGroup) filtering
 * across snapshot result components. Manages URL parameters and store state automatically.
 *
 * Usage:
 * Alpine.data('mySnapshot', () => ({
 *   ...peopleFiltersMixin(),
 *
 *   // Your component code here
 *   fetchAll() { // your fetch implementation }
 * }));
 */
export const peopleFiltersMixin = () => ({
  // Test property to verify mixin is applied
  peopleFiltersMixinApplied: true,

  /**
   * Resolver function for group names from group IDs
   * Takes groups data as parameter to avoid scope issues
   */
  resolveGroupNames(groups, groupIds) {
    if (!groups?.length || !groupIds?.length) {
      return [];
    }

    const selectedGroups = groups.filter(
      (group) =>
        groupIds.includes(String(group.id)) || groupIds.includes(group.id),
    );

    return selectedGroups.map((group) => group.name);
  },

  /**
   * Get current group IDs from store or URL
   */
  getCurrentGroupIds() {
    const storeGroupIds = this.$store?.snapshotResultFilters?.groupIds || [];
    const urlGroupIds = !storeGroupIds.length
      ? getQueryStringParams()
          .get("group_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || []
      : [];

    return storeGroupIds.length > 0 ? storeGroupIds : urlGroupIds;
  },

  /**
   * Handle changes to squad (team) selection
   * Clears group selection and updates URL parameters
   */
  changeSquadIds(squadIds) {
    // Use this.$store first (matches getter pattern), fallback to global store
    if (this.$store) {
      this.$store.snapshotResultFilters.squadIds = [...squadIds];
      this.$store.snapshotResultFilters.groupIds = [];
      this.$store.snapshotResultFilters.filterType = "teams";
    } else {
      const store = window.Alpine?.store?.("snapshotResultFilters");
      if (store) {
        store.squadIds = [...squadIds];
        store.groupIds = [];
        store.filterType = "teams";
      }
    }
    // Update URL parameters
    if (squadIds && squadIds.length > 0) {
      setQueryStringParam("squad_ids", squadIds.join(","));
    } else {
      deleteQueryStringParam("squad_ids");
    }
    deleteQueryStringParam("group_ids");
    // Refresh data
    this.fetchAll();
    // Call squad name resolution if available (some components have this)
    if (typeof this.determineSquadNamesFromSquadIds === "function") {
      this.determineSquadNamesFromSquadIds();
    }
  },
  isFilteringByGroups() {
    const filterType = this.$store?.snapshotResultFilters?.filterType;
    const groupIds = this.$store?.snapshotResultFilters?.groupIds || [];

    // Only consider it filtering by groups if filterType is "groups" AND we have actual group IDs
    return filterType === "groups" && groupIds.length > 0;
  },

  /**
   * Handle changes to group (Frontline::UserGroup) selection
   * Clears squad selection and updates URL parameters
   */
  changeGroupIds(groupIds) {
    // Use this.$store first (matches getter pattern), fallback to global store
    if (this.$store) {
      this.$store.snapshotResultFilters.groupIds = [...groupIds];
      this.$store.snapshotResultFilters.squadIds = [];
      this.$store.snapshotResultFilters.filterType = "groups";
    } else {
      const store = window.Alpine?.store?.("snapshotResultFilters");
      if (store) {
        store.groupIds = [...groupIds];
        store.squadIds = [];
        store.filterType = "groups";
      }
    }

    // Update URL parameters
    if (groupIds && groupIds.length > 0) {
      setQueryStringParam("group_ids", groupIds.join(","));
    } else {
      deleteQueryStringParam("group_ids");
    }
    deleteQueryStringParam("squad_ids");
    // Refresh data
    this.fetchAll();
  },
});
